/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useEffect, useMemo, useRef } from 'react';
import type { ShopperProducts } from '@/scapi';
import { useScapiFetcher } from '@/hooks/use-scapi-fetcher';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { extractSuggestionImageUrl } from '@/lib/product/product-utils';

interface ProductSuggestionWithId {
    productId?: string;
    image?: string;
}

/**
 * Fetches hi-res product images for search suggestions that arrive without an `image` URL.
 * `getSearchSuggestions` does not support `imgTypes`/`allImages`, so catalogs that only expose
 * hi-res images need a follow-up `getProducts` call to hydrate suggestion tiles.
 */
export function useSearchSuggestionProductImages(productSuggestions: ProductSuggestionWithId[] | undefined): {
    imageByProductId: Map<string, string>;
    isHydrating: boolean;
} {
    const config = useConfig();
    const tileViewType = config.search?.products?.images?.tile ?? 'hi-res';

    const productIdsNeedingImages = useMemo(() => {
        if (!productSuggestions?.length) {
            return [];
        }

        return productSuggestions
            .filter((suggestion) => suggestion.productId && !suggestion.image)
            .map((suggestion) => suggestion.productId as string);
    }, [productSuggestions]);

    const productIdsKey = productIdsNeedingImages.join(',');
    const lastFetchedIdsRef = useRef<string>('');

    const fetcher = useScapiFetcher('shopperProducts', 'getProducts', {
        params: {
            query: {
                ids: productIdsNeedingImages,
                expand: ['images'],
                allImages: true,
            },
        },
    });

    useEffect(() => {
        if (productIdsNeedingImages.length === 0 || productIdsKey === lastFetchedIdsRef.current) {
            return;
        }

        lastFetchedIdsRef.current = productIdsKey;
        void fetcher.load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productIdsKey, productIdsNeedingImages.length]);

    const imageByProductId = useMemo(() => {
        const response = fetcher.data as
            | { data?: ShopperProducts.schemas['Product'][]; limit?: number; total?: number }
            | undefined;
        const products = response?.data ?? [];
        if (products.length === 0) {
            return new Map<string, string>();
        }

        const map = new Map<string, string>();
        for (const product of products) {
            const imageUrl = extractSuggestionImageUrl(product, tileViewType);
            if (imageUrl && product.id) {
                map.set(product.id, imageUrl);
            }
        }

        return map;
    }, [fetcher.data, tileViewType]);

    const isHydrating =
        productIdsNeedingImages.length > 0 &&
        (productIdsKey !== lastFetchedIdsRef.current || fetcher.state === 'loading');

    return { imageByProductId, isHydrating };
}
